import React from 'react';
import { makeStyles } from '@mui/styles';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
} from '@mui/material';
import {
  TrendingUp,
  Lightbulb,
  School,
  Psychology,
  Timeline,
  Assessment,
  Speed,
  Security,
  Analytics,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { colors, typography, spacing, borderRadius, shadows, transitions, componentStyles } from '../styles/designSystem';

const useStyles = makeStyles((theme) => ({
  insights: {
    background: colors.gradients.hero,
    padding: `${spacing['4xl']} 0`,
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'radial-gradient(circle at 50% 50%, rgba(230, 0, 122, 0.05) 0%, transparent 70%)',
      pointerEvents: 'none',
    },
  },
  
  sectionContainer: {
    ...componentStyles.contentContainer,
    position: 'relative',
    zIndex: 2,
  },
  
  title: {
    fontFamily: typography.fontFamily.primary,
    fontWeight: typography.fontWeight.black,
    fontSize: typography.fontSize['4xl'],
    color: colors.text.primary,
    marginBottom: spacing.md,
    letterSpacing: typography.letterSpacing.tight,
    background: colors.gradients.primary,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  
  subtitle: {
    fontFamily: typography.fontFamily.secondary,
    fontWeight: typography.fontWeight.regular,
    fontSize: typography.fontSize.lg,
    color: colors.text.secondary,
    maxWidth: '800px',
    margin: '0 auto',
    lineHeight: typography.lineHeight.relaxed,
    letterSpacing: typography.letterSpacing.normal,
    marginBottom: spacing['3xl'],
  },
  
  insightCard: {
    ...componentStyles.glassCard,
    height: '100%',
    transition: transitions.smooth,
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: shadows.xl,
    },
  },
  
  tipCard: {
    ...componentStyles.glassCard,
    padding: spacing.lg,
    marginBottom: spacing.md,
    border: `1px solid rgba(255, 255, 255, 0.1)`,
  },
  
  tipIcon: {
    color: colors.primary.main,
    marginBottom: spacing.sm,
  },
  
  tipTitle: {
    fontFamily: typography.fontFamily.primary,
    fontWeight: typography.fontWeight.semiBold,
    fontSize: typography.fontSize.lg,
    color: colors.text.primary,
    marginBottom: spacing.sm,
  },
  
  tipDescription: {
    fontFamily: typography.fontFamily.secondary,
    fontWeight: typography.fontWeight.regular,
    fontSize: typography.fontSize.base,
    color: colors.text.secondary,
    lineHeight: typography.lineHeight.relaxed,
  },
  
  strategyCard: {
    ...componentStyles.glassCard,
    padding: spacing.lg,
    marginBottom: spacing.md,
  },
  
  strategyTitle: {
    fontFamily: typography.fontFamily.primary,
    fontWeight: typography.fontWeight.semiBold,
    fontSize: typography.fontSize.xl,
    color: colors.text.primary,
    marginBottom: spacing.md,
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
  },
  
  strategyDescription: {
    fontFamily: typography.fontFamily.secondary,
    fontWeight: typography.fontWeight.regular,
    fontSize: typography.fontSize.base,
    color: colors.text.secondary,
    lineHeight: typography.lineHeight.relaxed,
    marginBottom: spacing.lg,
  },
  
  strategyList: {
    '& .MuiListItem-root': {
      padding: `${spacing.sm} 0`,
    },
    '& .MuiListItemIcon-root': {
      minWidth: 'auto',
      marginRight: spacing.sm,
      color: colors.primary.main,
    },
  },
  
  riskLevel: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
    marginTop: spacing.md,
  },
  
  riskChip: {
    fontFamily: typography.fontFamily.secondary,
    fontWeight: typography.fontWeight.semiBold,
    fontSize: typography.fontSize.sm,
    padding: `${spacing.xs} ${spacing.sm}`,
    borderRadius: borderRadius.xl,
    textTransform: 'uppercase',
    letterSpacing: typography.letterSpacing.wider,
  },
  
  highRisk: {
    backgroundColor: colors.error.main,
    color: colors.text.primary,
  },
  
  mediumRisk: {
    backgroundColor: colors.warning.main,
    color: colors.text.primary,
  },
  
  lowRisk: {
    backgroundColor: colors.success.main,
    color: colors.text.primary,
  },
}));

const TradingInsights = () => {
  const classes = useStyles();

  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  const tradingTips = [
    {
      icon: <Psychology />,
      title: "Psychology is Key",
      description: "Emotional control is crucial in trading. Never let fear or greed drive your decisions. Stick to your strategy and risk management rules.",
      risk: "low",
    },
    {
      icon: <Timeline />,
      title: "Trend Following",
      description: "Trade with the trend, not against it. Use multiple timeframes to confirm trend direction and avoid fighting the market.",
      risk: "medium",
    },
    {
      icon: <Security />,
      title: "Risk Management",
      description: "Never risk more than 1-2% of your capital on a single trade. Use stop-losses and position sizing to protect your account.",
      risk: "low",
    },
    {
      icon: <Analytics />,
      title: "Technical Analysis",
      description: "Combine multiple indicators for confirmation. RSI, MACD, and moving averages work best when used together, not in isolation.",
      risk: "medium",
    },
    {
      icon: <Speed />,
      title: "Volume Analysis",
      description: "Volume confirms price action. High volume on breakouts increases the likelihood of continued movement in that direction.",
      risk: "medium",
    },
    {
      icon: <Assessment />,
      title: "Market Sentiment",
      description: "Monitor market sentiment but don't follow the crowd blindly. Extreme fear or greed often signals potential reversals.",
      risk: "high",
    },
  ];

  const tradingStrategies = [
    {
      title: "Swing Trading Strategy",
      icon: <TrendingUp />,
      description: "Medium-term trading strategy focusing on capturing larger price movements over days to weeks.",
      steps: [
        "Identify strong trends using multiple timeframes",
        "Wait for pullbacks to key support/resistance levels",
        "Enter positions with confirmed reversal signals",
        "Use 2:1 or better risk-reward ratios",
        "Hold positions for 3-7 days typically",
      ],
      risk: "medium",
    },
    {
      title: "Scalping Strategy",
      icon: <Speed />,
      description: "Short-term trading strategy for capturing small, quick profits from market volatility.",
      steps: [
        "Focus on high-volume, liquid cryptocurrencies",
        "Use 1-minute to 5-minute charts",
        "Enter and exit positions quickly",
        "Tight stop-losses (0.5-1% risk)",
        "Take profits at 1-2% gains",
      ],
      risk: "high",
    },
    {
      title: "Position Trading Strategy",
      icon: <Timeline />,
      description: "Long-term strategy for capturing major market trends over weeks to months.",
      steps: [
        "Analyze fundamental factors and market cycles",
        "Use weekly and monthly charts for entry",
        "Position size 5-10% of portfolio per trade",
        "Wide stop-losses (10-20% from entry)",
        "Hold through normal market volatility",
      ],
      risk: "low",
    },
  ];

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'high': return colors.error.main;
      case 'medium': return colors.warning.main;
      case 'low': return colors.success.main;
      default: return colors.text.secondary;
    }
  };

  const getRiskChipClass = (risk) => {
    switch (risk) {
      case 'high': return classes.highRisk;
      case 'medium': return classes.mediumRisk;
      case 'low': return classes.lowRisk;
      default: return '';
    }
  };

  return (
    <Box className={classes.insights}>
      <Container className={classes.sectionContainer}>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          <motion.div variants={itemVariants}>
            <Box textAlign="center" sx={{ mb: 6 }}>
              <Typography className={classes.title}>
                Professional Trading Insights
              </Typography>
              <Typography className={classes.subtitle}>
                Master the art of cryptocurrency trading with expert tips, proven strategies, 
                and professional analysis techniques used by successful traders.
              </Typography>
            </Box>
          </motion.div>

          <Grid container spacing={4}>
            {/* Trading Tips */}
            <Grid item xs={12} md={6}>
              <motion.div variants={itemVariants}>
                <Card className={classes.insightCard}>
                  <CardContent>
                    <Typography variant="h5" sx={{ color: colors.text.primary, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Lightbulb sx={{ color: colors.primary.main }} />
                      Essential Trading Tips
                    </Typography>
                    
                    {tradingTips.map((tip, index) => (
                      <Box key={index} className={classes.tipCard}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                          <Box className={classes.tipIcon}>
                            {tip.icon}
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography className={classes.tipTitle}>
                              {tip.title}
                            </Typography>
                            <Typography className={classes.tipDescription}>
                              {tip.description}
                            </Typography>
                            <Box className={classes.riskLevel}>
                              <Security sx={{ color: getRiskColor(tip.risk), fontSize: 16 }} />
                              <Chip 
                                label={`${tip.risk.charAt(0).toUpperCase() + tip.risk.slice(1)} Risk`}
                                className={`${classes.riskChip} ${getRiskChipClass(tip.risk)}`}
                                size="small"
                              />
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>

            {/* Trading Strategies */}
            <Grid item xs={12} md={6}>
              <motion.div variants={itemVariants}>
                <Card className={classes.insightCard}>
                  <CardContent>
                    <Typography variant="h5" sx={{ color: colors.text.primary, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <School sx={{ color: colors.primary.main }} />
                      Proven Trading Strategies
                    </Typography>
                    
                    {tradingStrategies.map((strategy, index) => (
                      <Box key={index} className={classes.strategyCard}>
                        <Typography className={classes.strategyTitle}>
                          {strategy.icon}
                          {strategy.title}
                        </Typography>
                        <Typography className={classes.strategyDescription}>
                          {strategy.description}
                        </Typography>
                        
                        <List className={classes.strategyList}>
                          {strategy.steps.map((step, stepIndex) => (
                            <ListItem key={stepIndex} sx={{ padding: 0 }}>
                              <ListItemIcon>
                                <Box sx={{ 
                                  width: 8, 
                                  height: 8, 
                                  borderRadius: '50%', 
                                  backgroundColor: colors.primary.main 
                                }} />
                              </ListItemIcon>
                              <ListItemText 
                                primary={step}
                                sx={{
                                  '& .MuiListItemText-primary': {
                                    color: colors.text.secondary,
                                    fontSize: typography.fontSize.sm,
                                    fontFamily: typography.fontFamily.secondary,
                                  }
                                }}
                              />
                            </ListItem>
                          ))}
                        </List>
                        
                        <Box className={classes.riskLevel}>
                          <Security sx={{ color: getRiskColor(strategy.risk), fontSize: 16 }} />
                          <Chip 
                            label={`${strategy.risk.charAt(0).toUpperCase() + strategy.risk.slice(1)} Risk Strategy`}
                            className={`${classes.riskChip} ${getRiskChipClass(strategy.risk)}`}
                            size="small"
                          />
                        </Box>
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          </Grid>

          {/* Professional Disclaimer */}
          <motion.div variants={itemVariants}>
            <Box sx={{ mt: 6 }}>
              <Alert severity="info" sx={{ 
                background: 'rgba(230, 0, 122, 0.1)', 
                border: `1px solid ${colors.primary.main}`,
                color: colors.text.primary,
                '& .MuiAlert-icon': {
                  color: colors.primary.main,
                }
              }}>
                <Typography variant="body2" sx={{ fontFamily: typography.fontFamily.secondary }}>
                  <strong>Professional Disclaimer:</strong> The information provided is for educational purposes only. 
                  Cryptocurrency trading involves substantial risk of loss and is not suitable for all investors. 
                  Past performance does not guarantee future results. Always conduct your own research and consider 
                  consulting with financial advisors before making investment decisions.
                </Typography>
              </Alert>
            </Box>
          </motion.div>
        </motion.div>
      </Container>
    </Box>
  );
};

export default TradingInsights; 